import React from 'react'
import { logger } from '../../logging'

import { Text, View, Flex, Table, TableHead, TableRow, TableCell, TableBody, Loader, Pagination, Button, Input, Card } from '@aws-amplify/ui-react'


import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client'
import * as models from '@mytmpvpn/mytmpvpn-common/models/vpn'
import { Location } from '@mytmpvpn/mytmpvpn-common/models/location'
import { DnsProvider, DNS_RESOLVERS } from '@mytmpvpn/mytmpvpn-common/models/vpnConfig'

import { MainTitle, SORT_ORDER_ICONS, Help, convertSecondsToHMSShort } from '../Common'
import { VpnWithMetrics } from '../../pages/Home'
import { Instructions } from '../static/Instructions'
import { Actions } from './Actions'
import { Metrics } from './Metrics'
import { Country } from '../Country'
import { City } from '../City'
import { useComponentSize } from '../ResponsiveSize'
import { isTerminalState, VpnState } from './VpnState'
import { DeprovisioningTips, ProvisioningTips } from './Tips'
import { VpnId } from './VpnId'
import { VpnConfig } from './VpnConfig'

const ITEMS_PER_PAGE = 5


function getSortIcon(sortOrder: models.ListVpnsSortOrder) {
  return SORT_ORDER_ICONS[sortOrder.toLowerCase() as keyof typeof SORT_ORDER_ICONS]
}

// Client-side filtering function
function filterVpnsWithMetrics(
  vpnsWithMetrics: VpnWithMetrics[],
  locations: Location[],
  searchTerm: string
): VpnWithMetrics[] {
  if (!searchTerm) return vpnsWithMetrics

  return vpnsWithMetrics.filter(vpnWithMetrics => {
    const location = locations.find(l => vpnWithMetrics.geonamesId === l.geonamesId)
    if (!location) return false

    const search = searchTerm.toLowerCase()
    return location.city.toLowerCase().includes(search) ||
      location.country.toLowerCase().includes(search)
  })
}

// Client-side sorting function
function sortVpnsWithMetrics(
  vpnsWithMetrics: VpnWithMetrics[],
  locations: Location[],
  sortBy: models.ListVpnsSortByCriteria,
  sortOrder: models.ListVpnsSortOrder
): VpnWithMetrics[] {
  const sorted = [...vpnsWithMetrics].sort((a, b) => {
    let compareResult = 0

    const aLocation = locations.find(l => a.geonamesId === l.geonamesId)
    const bLocation = locations.find(l => b.geonamesId === l.geonamesId)

    switch (sortBy) {
      case models.ListVpnsSortByCriteria.City:
        if (aLocation && bLocation) {
          compareResult = aLocation.city.localeCompare(bLocation.city)
        }
        break
      case models.ListVpnsSortByCriteria.Country:
        if (aLocation && bLocation) {
          compareResult = aLocation.country.localeCompare(bLocation.country)
        }
        break
      case models.ListVpnsSortByCriteria.GeonamesId:
        compareResult = a.geonamesId - b.geonamesId
        break
      case models.ListVpnsSortByCriteria.State:
        compareResult = models.toRank(a.state) - models.toRank(b.state)
        break
      case models.ListVpnsSortByCriteria.VpnId:
        compareResult = a.vpnId.localeCompare(b.vpnId)
        break
      case models.ListVpnsSortByCriteria.CreatedAt:
      default:
        compareResult = a.createdAt.getTime() - b.createdAt.getTime()
        break
    }

    return sortOrder === models.ListVpnsSortOrder.Desc ? -compareResult : compareResult
  })

  return sorted
}

// Client-side pagination function
function paginateVpns(vpns: VpnWithMetrics[], currentPage: number, itemsPerPage: number): VpnWithMetrics[] {
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  return vpns.slice(startIndex, endIndex)
}

function VpnIdLabel({ expandedId, setExpandedId }: { expandedId: boolean, setExpandedId: any }) {
  return <Button
    size="small"
    variation="link"
    onClick={() => setExpandedId(!expandedId)}
  >Id ⟷</Button>
}

function VpnsTableColumnHeader({
  label,
  sortByCriteria,
  selectedSortBy,
  setSelectedSortBy,
  sortOrder,
  setSortOrder }:
  {
    label: string,
    sortByCriteria: models.ListVpnsSortByCriteria,
    selectedSortBy: models.ListVpnsSortByCriteria,
    setSelectedSortBy: any,
    sortOrder: models.ListVpnsSortOrder,
    setSortOrder: any
  }
) {
  return <View>
    <Text
      style={{
        cursor: 'pointer'
      }}
      onClick={() => {
        if (selectedSortBy === sortByCriteria) {
          // If clicking on the same column, toggle sort order
          setSortOrder(sortOrder === models.ListVpnsSortOrder.Desc ? models.ListVpnsSortOrder.Asc : models.ListVpnsSortOrder.Desc)
        } else {
          // If clicking on a different column, set new sort criteria and default to Desc
          setSelectedSortBy(sortByCriteria)
          setSortOrder(models.ListVpnsSortOrder.Desc)
        }
      }}> {label} {selectedSortBy === sortByCriteria ? getSortIcon(sortOrder) : ''}
    </Text>
  </View >
}

function VpnMetrics({ vpnWithMetrics }: { vpnWithMetrics: VpnWithMetrics }) {
  return <View>
    {vpnWithMetrics.metrics &&
      <Flex alignItems={'center'}>
        <Metrics metrics={vpnWithMetrics.metrics} />
      </Flex>
    }
  </View>
}

export function getDnsLabel(dns?: DnsProvider): string {
  if (!dns) {
    return "Random"
  }
  const resolver = DNS_RESOLVERS[dns as DnsProvider]
  return resolver ? resolver.label : "Random"
}

function VpnStateDesktop({ vpnWithMetrics }: { vpnWithMetrics: VpnWithMetrics }) {
  const isDeprovisioning = vpnWithMetrics.state === models.VpnState.Deprovisioning
  return <View>
    <VpnState vpnWithMetrics={vpnWithMetrics} />
    {
      !isTerminalState(vpnWithMetrics.state) &&
      <Flex direction={'column'} gap={'0.25rem'} marginTop={'0.25rem'}>
        <Flex direction={'row'} alignItems={'center'}>
          <Loader marginRight={"0.5em"} />
          <Text fontSize={'smaller'}>{isDeprovisioning ? '~1mn' : '~3mn'}</Text>
        </Flex>
        {isDeprovisioning
          ? <DeprovisioningTips pollCount={vpnWithMetrics.pollCount ?? 0} />
          : <ProvisioningTips pollCount={vpnWithMetrics.pollCount ?? 0} />
        }
      </Flex>
    }
  </View>
}

function VpnCreationDate({ vpnWithMetrics }: { vpnWithMetrics: VpnWithMetrics }) {
  return <View>
    <Text>{vpnWithMetrics.createdAt.toLocaleString()}</Text>
  </View>
}


function VpnTableLine({ client, index, vpnWithMetrics, vpnsWithMetrics, setVpnsWithMetrics, locations, expandedId }:
  {
    client: MyTmpVpnClient,
    index: number,
    vpnWithMetrics: VpnWithMetrics,
    vpnsWithMetrics: VpnWithMetrics[],
    setVpnsWithMetrics: any,
    locations: Location[],
    expandedId: boolean
  }) {
  if (!locations) return <Loader variation='linear' />
  const location = locations.find(l => vpnWithMetrics.geonamesId === l.geonamesId)!
  logger.log('VpnTableLine:', vpnWithMetrics, 'Location:', location)
  return (
    <TableRow key={index}>
      <TableCell><VpnId vpnWithMetrics={vpnWithMetrics} expandedId={expandedId} /></TableCell>
      <TableCell><VpnCreationDate vpnWithMetrics={vpnWithMetrics} /></TableCell>
      <TableCell><Country location={location} /></TableCell>
      <TableCell><City location={location} /></TableCell>
      <TableCell><VpnConfig vpnWithMetrics={vpnWithMetrics} /></TableCell>
      <TableCell><VpnStateDesktop vpnWithMetrics={vpnWithMetrics} /></TableCell>
      <TableCell><VpnMetrics vpnWithMetrics={vpnWithMetrics} /></TableCell>
      <TableCell>
        {vpnWithMetrics.state === models.VpnState.Running &&
          <Actions
            client={client}
            vpnWithMetrics={vpnWithMetrics}
            vpnsWithMetrics={vpnsWithMetrics}
            setVpnsWithMetrics={setVpnsWithMetrics} />
        }
      </TableCell>
    </TableRow>
  )
}

/** Compact card view of a single VPN for mobile/short mode */
function VpnCard({ client, vpnWithMetrics, vpnsWithMetrics, setVpnsWithMetrics, locations }:
  {
    client: MyTmpVpnClient,
    vpnWithMetrics: VpnWithMetrics,
    vpnsWithMetrics: VpnWithMetrics[],
    setVpnsWithMetrics: any,
    locations: Location[]
  }) {
  const location = locations.find(l => vpnWithMetrics.geonamesId === l.geonamesId)!
  const compressedId = models.compressVpnId(vpnWithMetrics)

  return (
    <Card variation="outlined" padding="0.75rem" marginBottom="0.5rem">
      <Flex direction="column" gap="0.25rem">
        {/* Row 1: Location + State */}
        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap="0.5rem">
            <Country location={location} />
            <City location={location} />
          </Flex>
          <VpnState vpnWithMetrics={vpnWithMetrics} />
        </Flex>
        {/* Row 2: Id + date (compact) */}
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="0.7rem" fontFamily="monospace" color="gray">{compressedId}</Text>
          <Text fontSize="0.7rem" color="gray">{vpnWithMetrics.createdAt.toLocaleDateString()}</Text>
        </Flex>
        {/* Row 3: Provisioning loader or Metrics */}
        {!isTerminalState(vpnWithMetrics.state) ? (
          <Flex direction="column" gap="0.25rem" padding="0.25rem">
            <Flex alignItems="center" gap="0.5rem" justifyContent="center">
              <Loader size="small" />
              <Text fontSize="0.75rem" color="gray">{vpnWithMetrics.state === models.VpnState.Deprovisioning ? '~1mn' : '~3mn'}</Text>
            </Flex>
            {vpnWithMetrics.state === models.VpnState.Deprovisioning
              ? <DeprovisioningTips pollCount={vpnWithMetrics.pollCount ?? 0} />
              : <ProvisioningTips pollCount={vpnWithMetrics.pollCount ?? 0} />
            }
          </Flex>
        ) : (
          vpnWithMetrics.metrics && (
            <Flex alignItems="center" justifyContent="center" gap="0.75rem" wrap="wrap">
              <Text fontSize="0.7rem">🥜 {vpnWithMetrics.metrics.peanuts !== null ? vpnWithMetrics.metrics.peanuts.toFixed(2) : 'N/A'}</Text>
              <Text fontSize="0.7rem">⏳ {vpnWithMetrics.metrics.duration !== null ? convertSecondsToHMSShort(vpnWithMetrics.metrics.duration) : 'N/A'}</Text>
              <Text fontSize="0.7rem">↔️ {vpnWithMetrics.metrics.bytes !== null ? `${(vpnWithMetrics.metrics.bytes / 1024 / 1024).toFixed(2)} MiB` : 'N/A'}</Text>
            </Flex>
          )
        )}
        {/* Row 4: Actions (only when running) */}
        {vpnWithMetrics.state === models.VpnState.Running &&
          <Flex justifyContent="flex-end">
            <Actions
              client={client}
              vpnWithMetrics={vpnWithMetrics}
              vpnsWithMetrics={vpnsWithMetrics}
              setVpnsWithMetrics={setVpnsWithMetrics} />
          </Flex>
        }
      </Flex>
    </Card>
  )
}

export function Vpns({ client,
  vpnsWithMetrics,
  setVpnsWithMetrics,
  locations,
  isLoading }: {
    client: MyTmpVpnClient,
    vpnsWithMetrics: VpnWithMetrics[],
    setVpnsWithMetrics: any,
    locations: Location[],
    setError: any,
    isLoading?: boolean,
  }) {
  // logger.log(`Vpns list: ${JSON.stringify(vpnsWithMetrics.map(v => v.vpn))}`)

  const [expandedId, setExpandedId] = React.useState<boolean>(false)
  const [searchTerm, setSearchTerm] = React.useState<string>("")
  const [sortBy, setSortBy] = React.useState<models.ListVpnsSortByCriteria>(models.ListVpnsSortByCriteria.CreatedAt)
  const [sortOrder, setSortOrder] = React.useState<models.ListVpnsSortOrder>(models.ListVpnsSortOrder.Desc)
  const [currentPage, setCurrentPage] = React.useState<number>(1)
  const componentSize = useComponentSize()

  // Apply client-side filtering and sorting
  const filteredVpns = React.useMemo(() => {
    if (!vpnsWithMetrics || !locations) return []
    return filterVpnsWithMetrics(vpnsWithMetrics, locations, searchTerm)
  }, [vpnsWithMetrics, locations, searchTerm])

  const sortedVpns = React.useMemo(() => {
    if (!filteredVpns || !locations) return []
    return sortVpnsWithMetrics(filteredVpns, locations, sortBy, sortOrder)
  }, [filteredVpns, locations, sortBy, sortOrder])

  // Calculate pagination
  const totalPages = Math.ceil(sortedVpns.length / ITEMS_PER_PAGE)
  const paginatedVpns = React.useMemo(() => {
    return paginateVpns(sortedVpns, currentPage, ITEMS_PER_PAGE)
  }, [sortedVpns, currentPage])

  // Reset to page 1 when filters or sorting change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortBy, sortOrder])

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handlePageChange = (pageIndex: number | undefined) => {
    if (pageIndex && pageIndex >= 1 && pageIndex <= totalPages) {
      setCurrentPage(pageIndex)
    }
  }

  logger.log(`currentPage: ${currentPage}; totalPages: ${totalPages}; filteredVpns: ${filteredVpns.length}; sortedVpns: ${sortedVpns.length}`)
  logger.debug(`vpnsWithMetrics: ${JSON.stringify(vpnsWithMetrics)}`)

  return (
    <View>
      <View id="your-vpns">
        <MainTitle>Your Vpns</MainTitle>
      </View>
      <Flex justifyContent="center" marginBottom={'1em'}>
        <Help
          initialState={(isLoading || vpnsWithMetrics.length > 0) ? 'collapsed' : 'expanded'}
          helpComponent={<Instructions />}>
          ℹ️ Instructions
        </Help>
      </Flex>

      {isLoading && (
        <Flex justifyContent="center" alignItems="center" padding="1rem">
          <Loader size="large" />
          <Text marginLeft="0.5rem">Refreshing VPNs...</Text>
        </Flex>
      )}

      {/* Search Filter */}
      <View marginBottom="1rem">
        <Input
          placeholder="Search by city or country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </View>

      {/* Show results count */}
      <Text fontSize="small" marginBottom="1rem" color="gray">
        Showing {paginatedVpns.length} of {sortedVpns.length} VPNs
        {searchTerm && ` (filtered from ${vpnsWithMetrics?.length || 0} total)`}
      </Text>

      {/* Pagination - only show if there are multiple pages */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasMorePages={currentPage < totalPages}
          onNext={handleNextPage}
          onPrevious={handlePrevPage}
          onChange={handlePageChange}
        />
      )}

      {/* Short (mobile) mode: compact card list */}
      {componentSize === 'short' ? (
        <View>
          {paginatedVpns.length > 0 ?
            paginatedVpns.map((vpnWithMetrics) => (
              <VpnCard
                key={vpnWithMetrics.vpnId}
                client={client}
                vpnWithMetrics={vpnWithMetrics}
                vpnsWithMetrics={vpnsWithMetrics}
                setVpnsWithMetrics={setVpnsWithMetrics}
                locations={locations} />
            ))
            :
            vpnsWithMetrics === null || vpnsWithMetrics === undefined ?
              <Loader variation='linear' />
              :
              <Text textAlign="center" color="gray">
                {searchTerm ? 'No VPNs match your search' : 'No VPNs found'}
              </Text>
          }
        </View>
      ) : (
        /* Standard (desktop) mode: full table */
        <div className="responsive-table-wrapper">
          <Table
            highlightOnHover={true}
            variation="striped">
            <TableHead>
              <TableRow>
                <TableCell as="th">
                  <VpnIdLabel expandedId={expandedId} setExpandedId={setExpandedId} />
                </TableCell>
                <TableCell as="th">
                  <VpnsTableColumnHeader
                    label='Created At'
                    sortByCriteria={models.ListVpnsSortByCriteria.CreatedAt}
                    selectedSortBy={sortBy}
                    setSelectedSortBy={setSortBy}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder} />
                </TableCell>
                <TableCell as="th"><VpnsTableColumnHeader
                  label='Country'
                  sortByCriteria={models.ListVpnsSortByCriteria.Country}
                  selectedSortBy={sortBy}
                  setSelectedSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder} /></TableCell>
                <TableCell as="th"><VpnsTableColumnHeader
                  label='City'
                  sortByCriteria={models.ListVpnsSortByCriteria.City}
                  selectedSortBy={sortBy}
                  setSelectedSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder} /></TableCell>
                <TableCell as="th">Config</TableCell>
                <TableCell as="th"><VpnsTableColumnHeader
                  label='State'
                  sortByCriteria={models.ListVpnsSortByCriteria.State}
                  selectedSortBy={sortBy}
                  setSelectedSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder} /></TableCell>
                <TableCell as="th">Usage</TableCell>
                <TableCell as="th">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                paginatedVpns.length > 0 ?
                  paginatedVpns.map((vpnWithMetrics, index) => (
                    <VpnTableLine
                      key={vpnWithMetrics.vpnId}
                      client={client}
                      index={index}
                      vpnWithMetrics={vpnWithMetrics}
                      vpnsWithMetrics={vpnsWithMetrics}
                      setVpnsWithMetrics={setVpnsWithMetrics}
                      locations={locations}
                      expandedId={expandedId} />
                  ))
                  :
                  vpnsWithMetrics === null || vpnsWithMetrics === undefined ?
                    <TableRow>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                      <TableCell><Loader variation='linear' /></TableCell>
                    </TableRow>
                    :
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Text textAlign="center" color="gray">
                          {searchTerm ? 'No VPNs match your search' : 'No VPNs found'}
                        </Text>
                      </TableCell>
                    </TableRow>
              }
            </TableBody>
          </Table >
        </div>
      )}

      {/* Pagination at bottom - only show if there are multiple pages */}
      {totalPages > 1 && (
        <View marginTop="1rem">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasMorePages={currentPage < totalPages}
            onNext={handleNextPage}
            onPrevious={handlePrevPage}
            onChange={handlePageChange}
          />
        </View>
      )}
    </View>
  )
}
